import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  #abortController = new AbortController();
  #targetAddToCartButton = null;
  #currentQuantity = 1;
  #isStuck = false;
  #resetTimeout = null;
  #scrollTicking = false;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');
    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.#refreshTargetButton();
    this.#getInitialQuantity();
    this.#checkStickyPosition();
    window.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    window.addEventListener('resize', this.#checkStickyPosition, { passive: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
  }

  #handleScroll = () => {
    if (this.#scrollTicking) return;
    this.#scrollTicking = true;
    requestAnimationFrame(() => {
      this.#scrollTicking = false;
      this.#checkStickyPosition();
    });
  };

  #refreshTargetButton() {
    this.#targetAddToCartButton = this.#getProductForm()?.querySelector('[ref="addToCartButton"]') ?? null;
    return this.#targetAddToCartButton;
  }

  #checkStickyPosition = () => {
    const targetButton = this.#refreshTargetButton();
    if (!targetButton) return;
    const headerOffset = window.matchMedia('(max-width: 749px)').matches ? 80 : 0;
    const rect = targetButton.getBoundingClientRect();
    const buttonPassed = rect.bottom <= headerOffset;
    if (buttonPassed && !this.#isStuck && !this.#isChatActive()) {
      this.#showStickyBar();
    } else if (!buttonPassed && this.#isStuck) {
      this.#hideStickyBar();
    }
  };

  handleAddToCartClick = async () => {
    const targetButton = this.#refreshTargetButton();
    if (!targetButton || targetButton.disabled) return;
    this.#targetAddToCartButton = targetButton;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    this.refs.addToCartButton.dataset.added = 'true';
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = setTimeout(() => this.refs.addToCartButton.removeAttribute('data-added'), 800);
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !this.refs.productImage) return;
    const flyToCartElement = document.createElement('fly-to-cart');
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);
    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise.then(({ detail }) => {
      if (!detail?.html) return;
      const { html, productId, resource: variant } = detail;
      if (productId && productId !== this.dataset.productId) return;
      const newSticky = html.querySelector('sticky-add-to-cart');
      const newBar = newSticky?.querySelector('[ref="stickyBar"]');
      if (!newBar) return;
      const stuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
      morph(this.refs.stickyBar, newBar, { childrenOnly: true });
      this.refs.stickyBar.setAttribute('data-stuck', stuck);
      this.dataset.variantAvailable = newSticky.dataset.variantAvailable;
      if (variant?.id) this.dataset.currentVariantId = variant.id;
      this.#refreshTargetButton();
      this.#updateButtonText();
      this.#checkStickyPosition();
    }).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Variant update failed:', error);
    });
  };

  #handleCartUpdate = (event) => {
    const reset = () => {
      if (this.#targetAddToCartButton) this.#targetAddToCartButton.dataset.puppet = 'false';
    };
    event.promise?.finally(reset);
  };

  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;
    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #showStickyBar() {
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }

  #isChatActive() {
    const chat = document.querySelector('shopify-chat');
    return Boolean(chat?.hasAttribute('open'));
  }

  #getProductForm() {
    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section');
    if (!productId || !section) return null;
    const sectionId = section.id.replace('shopify-section-', '');
    return document.querySelector(`#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`);
  }

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1', 10) || 1;
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    const available = !addToCartButton.disabled;
    quantityNumber.textContent = String(this.#currentQuantity);
    quantityDisplay.style.display = available && this.#currentQuantity > 1 ? 'inline' : 'none';
  }
}

if (!customElements.get('sticky-add-to-cart')) customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
